import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Boxes, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    const d = value;
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  if (typeof value === 'string') {
    const parts = value.split('-').map((part) => parseInt(part, 10));
    if (parts.length === 3 && !parts.some(Number.isNaN)) {
      const [year, month, day] = parts;
      return new Date(year, month - 1, day);
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatDateInput = (date) => {
  const d = toDateOnly(date);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildTimelineDays = (from, to) => {
  const start = toDateOnly(from);
  const end = toDateOnly(to);
  if (!start || !end || start > end) {
    return [];
  }
  const days = [];
  const current = new Date(start);
  const todayKey = new Date().toISOString().slice(0, 10);
  const weekdayLabels = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  while (current <= end && days.length < 365) {
    const key = current.toISOString().slice(0, 10);
    days.push({
      key,
      date: new Date(current),
      dayLabel: String(current.getDate()).padStart(2, '0'),
      weekdayLabel: weekdayLabels[current.getDay()],
      isToday: key === todayKey
    });
    current.setDate(current.getDate() + 1);
  }

  return days;
};

const getEventColor = (eventType) => {
  switch (eventType) {
    case 'ASSIGNMENT':
      return '#4f46e5';
    case 'MAINTENANCE':
      return '#059669';
    case 'FAULT':
      return '#f97316';
    case 'TRANSFER':
      return '#0ea5e9';
    case 'REPLACEMENT':
      return '#a855f7';
    case 'DISPOSAL':
      return '#dc2626';
    case 'BOOKING':
      return '#6366f1';
    default:
      return '#6b7280';
  }
};

const getEventTypeLabel = (eventType) => {
  switch (eventType) {
    case 'ASSIGNMENT':
      return 'Assegnazione';
    case 'MAINTENANCE':
      return 'Manutenzione';
    case 'FAULT':
      return 'Guasto';
    case 'TRANSFER':
      return 'Trasferimento';
    case 'REPLACEMENT':
      return 'Sostituzione';
    case 'DISPOSAL':
      return 'Dismissione';
    case 'BOOKING':
      return 'Prenotazione';
    default:
      return eventType || 'Evento';
  }
};

const getAssignmentTypeLabel = (assignmentType) => {
  switch (assignmentType) {
    case 'SEDE':
      return 'Uso in sede';
    case 'SMART_WORKING':
      return 'Smart working';
    case 'TRASFERTA':
      return 'Trasferta';
    case 'MISTO':
      return 'Misto';
    default:
      return assignmentType || '-';
  }
};

export default function AssetCalendar() {
  const today = new Date();
  const initialYear = today.getFullYear();
  const initialMonth = today.getMonth();
  const weekStart = new Date(initialYear, initialMonth, today.getDate());
  const dayOfWeek = weekStart.getDay(); // 0-6 (Sun-Sat)
  const offset = (dayOfWeek + 6) % 7; // Monday = 0
  weekStart.setDate(weekStart.getDate() - offset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const [from, setFrom] = useState(formatDateInput(weekStart));
  const [to, setTo] = useState(formatDateInput(weekEnd));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [activeEventType, setActiveEventType] = useState('ALL');

  const rangeStart = toDateOnly(from);
  const rangeEnd = toDateOnly(to);
  const displayStart = useMemo(() => {
    if (!rangeStart) return null;
    const start = new Date(rangeStart);
    const day = start.getDay();
    const off = (day + 6) % 7;
    start.setDate(start.getDate() - off);
    return start;
  }, [rangeStart]);
  const displayEnd = useMemo(() => {
    if (!displayStart) return null;
    const end = new Date(displayStart);
    end.setDate(end.getDate() + 6);
    return end;
  }, [displayStart]);
  const days = useMemo(() => {
    if (!displayStart || !displayEnd) {
      return [];
    }
    const fromStr = formatDateInput(displayStart);
    const toStr = formatDateInput(displayEnd);
    return buildTimelineDays(fromStr, toStr);
  }, [displayStart, displayEnd]);
  const totalDays = days.length;

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const params = {
        page: 1,
        limit: 500
      };
      const res = await api.get('/assets', { params });
      const payload = res.data || {};
      const list = payload.data || [];
      setAssets(list);
    } catch (error) {
      console.error('Error loading assets for planner', error);
      toast.error('Errore nel caricamento degli asset');
    } finally {
      setAssetsLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!displayStart || !displayEnd || displayStart > displayEnd) {
      return;
    }

    setLoading(true);
    try {
      const fromDate = formatDateInput(displayStart);
      const toDate = formatDateInput(displayEnd);
      const params = {
        from: `${fromDate}T00:00:00`,
        to: `${toDate}T23:59:59`
      };
      const res = await api.get('/asset-calendar', { params });
      const data = res.data;
      const list = Array.isArray(data) ? data : [];
      setEvents(list);
    } catch (error) {
      console.error('Error loading asset calendar events', error);
      toast.error('Errore nel caricamento degli eventi calendario asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    if (displayStart && displayEnd) {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, displayStart, displayEnd]);

  const assetRows = useMemo(() => {
    const listAssets = Array.isArray(assets) ? assets : [];
    const listEvents = Array.isArray(events) ? events : [];

    const eventsByAsset = new Map();
    listEvents.forEach((e) => {
      if (!e.asset_id) return;
      if (!eventsByAsset.has(e.asset_id)) {
        eventsByAsset.set(e.asset_id, []);
      }
      eventsByAsset.get(e.asset_id).push(e);
    });

    listAssets.forEach((asset) => {
      const arr = eventsByAsset.get(asset.id);
      if (arr) {
        arr.sort((a, b) => {
          const aTime = new Date(a.start_at).getTime();
          const bTime = new Date(b.start_at).getTime();
          if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
          return aTime - bTime;
        });
      }
    });

    const rows = listAssets
      .map((asset) => {
        const allEvents = eventsByAsset.get(asset.id) || [];
        const filteredEvents =
          activeEventType === 'ALL'
            ? allEvents
            : activeEventType === 'AVAILABLE'
              ? []
              : allEvents.filter((e) => e.event_type === activeEventType);

        return {
          asset_id: asset.id,
          asset_code:
            asset.asset_code || asset.code || asset.name || `Asset #${asset.id}`,
          asset_description: asset.description || '',
          events: filteredEvents,
          totalEvents: allEvents.length
        };
      })
      .filter((row) => {
        if (activeEventType === 'ALL') return true;
        if (activeEventType === 'AVAILABLE') return row.totalEvents === 0;
        return row.events.length > 0;
      });

    rows.sort((a, b) => {
      const aCode = a.asset_code || '';
      const bCode = b.asset_code || '';
      return aCode.localeCompare(bCode);
    });

    return rows;
  }, [assets, events, activeEventType]);

  const periodLabel = useMemo(() => {
    if (!displayStart || !displayEnd) {
      return '';
    }
    const formatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    const fromLabel = displayStart.toLocaleDateString('it-IT', formatOptions);
    const toLabel = displayEnd.toLocaleDateString('it-IT', formatOptions);
    return `${fromLabel} - ${toLabel}`;
  }, [displayStart, displayEnd]);

  const handleTodayRange = () => {
    const base = new Date();
    const y = base.getFullYear();
    const m = base.getMonth();
    const d = base.getDate();
    const start = new Date(y, m, d);
    const day = start.getDay();
    const off = (day + 6) % 7;
    start.setDate(start.getDate() - off);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    setFrom(formatDateInput(start));
    setTo(formatDateInput(end));
  };

  const shiftWeek = (delta) => {
    if (!displayStart) return;
    const start = new Date(displayStart);
    start.setDate(start.getDate() + delta * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    setFrom(formatDateInput(start));
    setTo(formatDateInput(end));
  };

  useEffect(() => {
    if (!selectedAssetId && assetRows.length) {
      setSelectedAssetId(assetRows[0].asset_id);
    }
  }, [assetRows, selectedAssetId]);

  const selectedAssignments = useMemo(() => {
    if (!selectedAssetId) return [];
    const list = Array.isArray(events) ? events : [];
    const filtered = list.filter((e) => {
      if (e.asset_id !== selectedAssetId || e.event_type !== 'ASSIGNMENT') {
        return false;
      }
      if (!displayStart || !displayEnd || !e.start_at) {
        return true;
      }
      const startDate = toDateOnly(e.start_at);
      const endSource =
        e.end_expected_at ||
        e.end_actual_at ||
        formatDateInput(displayEnd);
      const endDate = toDateOnly(endSource);
      if (!startDate || !endDate) return false;
      if (endDate < displayStart || startDate > displayEnd) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      const aTime = new Date(a.start_at).getTime();
      const bTime = new Date(b.start_at).getTime();
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
      return aTime - bTime;
    });
  }, [events, selectedAssetId, displayStart, displayEnd]);

  const [assetPage, setAssetPage] = useState(0);
  const [assetPageSize, setAssetPageSize] = useState(10);
  const totalAssetPages = Math.max(1, Math.ceil(assetRows.length / assetPageSize));
  const pagedAssetRows = useMemo(
    () =>
      assetRows.slice(
        assetPage * assetPageSize,
        assetPage * assetPageSize + assetPageSize
      ),
    [assetRows, assetPage, assetPageSize]
  );

  useEffect(() => {
    if (assetPage >= totalAssetPages) {
      setAssetPage(0);
    }
  }, [assetPage, totalAssetPages]);

  const rowHeight = 40;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Boxes size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Planner asset
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ogni riga rappresenta un bene aziendale, con le assegnazioni e le manutenzioni
              distribuite sulla linea temporale.
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleTodayRange}
            >
              Oggi
            </Button>
            <IconButton
              size="small"
              onClick={() => shiftWeek(-1)}
            >
              <ChevronLeft size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => shiftWeek(1)}
            >
              <ChevronRight size={18} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {loading ? <CircularProgress size={16} /> : null}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {periodLabel}
            </Typography>
          </Box>
        </Box>
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              {[
                { type: 'ALL', label: 'Tutti', color: '#6b7280' },
                { type: 'AVAILABLE', label: 'Disponibile', color: '#10b981' },
                { type: 'ASSIGNMENT', label: getEventTypeLabel('ASSIGNMENT'), color: getEventColor('ASSIGNMENT') },
                { type: 'MAINTENANCE', label: getEventTypeLabel('MAINTENANCE'), color: getEventColor('MAINTENANCE') },
                { type: 'FAULT', label: getEventTypeLabel('FAULT'), color: getEventColor('FAULT') },
                { type: 'TRANSFER', label: getEventTypeLabel('TRANSFER'), color: getEventColor('TRANSFER') },
                { type: 'REPLACEMENT', label: getEventTypeLabel('REPLACEMENT'), color: getEventColor('REPLACEMENT') },
                { type: 'DISPOSAL', label: getEventTypeLabel('DISPOSAL'), color: getEventColor('DISPOSAL') },
                { type: 'BOOKING', label: getEventTypeLabel('BOOKING'), color: getEventColor('BOOKING') }
              ].map((item) => {
                const isActive = activeEventType === item.type;
                return (
                  <Box
                    key={item.type}
                    onClick={() => setActiveEventType(item.type)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mr: 1.5,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 999,
                      cursor: 'pointer',
                      bgcolor: isActive ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 999,
                        bgcolor: item.color
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={isActive ? 'text.primary' : 'text.secondary'}
                      sx={{ fontWeight: isActive ? 600 : 400 }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {(!displayStart || !displayEnd || totalDays === 0) && (
              <Typography variant="body2" color="text.secondary">
                Seleziona un intervallo di date valido per visualizzare il planner.
              </Typography>
            )}

            {displayStart && displayEnd && totalDays > 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  alignItems: 'stretch',
                  overflowX: 'auto'
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 260px',
                    borderRight: '1px solid rgba(145, 158, 171, 0.16)',
                    pr: 2
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600 }}
                    >
                      Asset
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {assetRows.length} risorsa/e
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 1,
                      border: '1px solid rgba(145, 158, 171, 0.16)',
                      bgcolor: 'background.paper'
                    }}
                  >
                    {assetsLoading ? (
                      <Box
                        sx={{
                          p: 1.5
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Caricamento asset...
                        </Typography>
                      </Box>
                    ) : assetRows.length === 0 ? (
                      <Box
                        sx={{
                          p: 1.5
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Nessun evento nel periodo selezionato.
                        </Typography>
                      </Box>
                    ) : (
                      pagedAssetRows.map((row) => (
                        <Box
                          key={row.asset_id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: rowHeight,
                            px: 1.5,
                            borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
                            cursor: 'pointer',
                            bgcolor:
                              selectedAssetId === row.asset_id
                                ? 'rgba(79, 70, 229, 0.06)'
                                : 'transparent'
                          }}
                          onClick={() => setSelectedAssetId(row.asset_id)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color:
                                selectedAssetId === row.asset_id
                                  ? 'primary.main'
                                  : 'text.primary'
                            }}
                          >
                            {row.asset_code}
                          </Typography>
                          {row.asset_description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {row.asset_description}
                            </Typography>
                          ) : null}
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 600,
                    pl: 2
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${totalDays}, minmax(24px, 1fr))`,
                      columnGap: 0.5,
                      mb: 1
                    }}
                  >
                    {days.map((day) => (
                      <Box
                        key={day.key}
                        sx={{
                          textAlign: 'center',
                          fontSize: 12,
                          color: day.isToday ? 'primary.main' : 'text.secondary'
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', fontWeight: day.isToday ? 700 : 500 }}
                        >
                          {day.dayLabel}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: 10 }}
                        >
                          {day.weekdayLabel}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 1,
                      border: '1px solid rgba(145, 158, 171, 0.16)',
                      bgcolor: 'background.paper'
                    }}
                  >
                    {pagedAssetRows.length === 0 ? (
                      <Box
                        sx={{
                          p: 1.5
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Nessuna barra pianificata da mostrare nel periodo selezionato.
                        </Typography>
                      </Box>
                    ) : (
                      pagedAssetRows.map((row) => (
                        <Box
                          key={row.asset_id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${totalDays}, minmax(24px, 1fr))`,
                            columnGap: 0.5,
                            alignItems: 'center',
                            height: rowHeight,
                            px: 0.5,
                            borderBottom: '1px solid rgba(145, 158, 171, 0.08)'
                          }}
                        >
                          {row.events.map((e) => {
                            if (!e.start_at) return null;

                            const startDate = toDateOnly(e.start_at);
                            const endSource =
                              e.end_expected_at ||
                              e.end_actual_at ||
                              (displayEnd ? displayEnd.toISOString() : e.start_at);
                            const endDate = toDateOnly(endSource);

                            if (!startDate || !endDate) return null;
                            if (endDate < displayStart || startDate > displayEnd) return null;

                            const clampedStart =
                              startDate < displayStart ? displayStart : startDate;
                            const clampedEnd =
                              endDate > displayEnd ? displayEnd : endDate;

                            const startIndex = Math.floor(
                              (clampedStart.getTime() - rangeStart.getTime()) / MS_PER_DAY
                            );
                            const endIndex = Math.floor(
                              (clampedEnd.getTime() - rangeStart.getTime()) / MS_PER_DAY
                            );

                            if (Number.isNaN(startIndex) || Number.isNaN(endIndex)) {
                              return null;
                            }

                            const safeStart = Math.max(
                              0,
                              Math.min(totalDays - 1, startIndex)
                            );
                            const safeEnd = Math.max(
                              safeStart,
                              Math.min(totalDays - 1, endIndex)
                            );

                            const color = getEventColor(e.event_type);
                            const label = getEventTypeLabel(e.event_type);

                            const titleLines = [];
                            titleLines.push(
                              `${row.asset_code}${row.asset_description ? ` – ${row.asset_description}` : ''}`
                            );
                            titleLines.push(`${label} (${e.status || 'PLANNED'})`);
                            titleLines.push(
                              `Dal ${new Date(e.start_at).toLocaleString('it-IT')}`
                            );
                            if (e.end_expected_at || e.end_actual_at) {
                              titleLines.push(
                                `Al ${
                                  new Date(
                                    e.end_expected_at || e.end_actual_at
                                  ).toLocaleString('it-IT')
                                }`
                              );
                            }
                            if (e.user_name) {
                              titleLines.push(
                                `Utente: ${e.user_name}${
                                  e.user_email ? ` (${e.user_email})` : ''
                                }`
                              );
                            }
                            if (e.title) {
                              titleLines.push(`Titolo: ${e.title}`);
                            }
                            if (e.description) {
                              titleLines.push(`Note: ${e.description}`);
                            }

                            return (
                              <Tooltip
                                key={e.id}
                                title={titleLines.join('\n')}
                                arrow
                              >
                                <Box
                                  sx={{
                                    gridColumn: `${safeStart + 1} / ${safeEnd + 2}`,
                                    borderRadius: 999,
                                    bgcolor: color,
                                    color: '#fff',
                                    fontSize: 11,
                                    px: 1,
                                    py: 0.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxShadow:
                                      '0 1px 2px rgba(15, 23, 42, 0.12), 0 1px 1px rgba(15, 23, 42, 0.10)',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'default'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                                      mr: 0.75,
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 500,
                                      color: 'rgba(255, 255, 255, 0.95)'
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Mostra
              </Typography>
              <Select
                size="small"
                value={assetPageSize}
                onChange={(event) => {
                  const next = Number(event.target.value) || 10;
                  setAssetPageSize(next);
                  setAssetPage(0);
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0.25,
                    px: 1,
                    fontSize: 12
                  }
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary">
                per pagina
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 2 }}
              >
                Pagina {assetPage + 1} di {totalAssetPages}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                disabled={assetPage === 0}
                onClick={() => setAssetPage((prev) => Math.max(0, prev - 1))}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <IconButton
                size="small"
                disabled={assetPage >= totalAssetPages - 1}
                onClick={() =>
                  setAssetPage((prev) => Math.min(totalAssetPages - 1, prev + 1))
                }
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>
          </Box>
          {selectedAssetId && (
            <Box
              sx={{
                mt: 3,
                borderTop: '1px solid rgba(145, 158, 171, 0.12)',
                pt: 2
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                Assegnazioni
              </Typography>
              {selectedAssignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nessuna assegnazione nel periodo selezionato per questo asset.
                </Typography>
              ) : (
                <Box
                  sx={{
                    borderRadius: 1,
                    border: '1px solid rgba(145, 158, 171, 0.16)',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1fr 120px 120px 180px 180px',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Stato
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Assegnatario
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Sede
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Tipo
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Inizio
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Fine
                    </Typography>
                  </Box>
                  {selectedAssignments.map((e) => (
                    <Box
                      key={e.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr 120px 120px 180px 180px',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderTop: '1px solid rgba(145, 158, 171, 0.08)',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2">
                        {e.assignment_status || e.status || '-'}
                      </Typography>
                      <Typography variant="body2">
                        {e.user_name
                          ? `${e.user_name}${e.user_email ? ` (${e.user_email})` : ''}`
                          : '-'}
                      </Typography>
                      <Typography variant="body2">
                        {e.office_name || '-'}
                      </Typography>
                      <Typography variant="body2">
                        {getAssignmentTypeLabel(e.assignment_type)}
                      </Typography>
                      <Typography variant="body2">
                        {e.start_at
                          ? new Date(e.start_at).toLocaleString('it-IT')
                          : '-'}
                      </Typography>
                      <Typography variant="body2">
                        {e.end_expected_at || e.end_actual_at
                          ? new Date(e.end_expected_at || e.end_actual_at).toLocaleString(
                              'it-IT'
                            )
                          : '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
}
